// src/components/common

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const CustomSelect = ({ 
  name, 
  value, 
  onChange, 
  options, 
  placeholder = 'Select...',
  className = ''
}) => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Select Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 rounded-lg transition-all appearance-none cursor-pointer text-left flex items-center justify-between ${
          isDarkMode
            ? 'bg-fsgu-gray-700/50 hover:bg-fsgu-gray-700 text-fsgu-silver-100 focus:bg-fsgu-gray-700 focus:ring-2 focus:ring-fsgu-green-500/30'
            : 'bg-fsgu-silver-100 hover:bg-fsgu-silver-200 text-fsgu-silver-900 focus:bg-white focus:ring-2 focus:ring-fsgu-green-500/30'
        } focus:outline-none border-0 ${!selectedOption && 'text-opacity-50'}`}
      >
        <span>{displayText}</span>
        <ChevronDown 
          className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''} ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className={`absolute z-50 w-full mt-2 rounded-xl shadow-lg overflow-hidden border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-white border-fsgu-silver-200'
          }`}
          style={{ maxHeight: '240px', overflowY: 'auto' }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`w-full px-4 py-3 text-left transition-colors ${
                value === option.value
                  ? isDarkMode
                    ? 'bg-fsgu-green-500/20 text-fsgu-green-400'
                    : 'bg-fsgu-green-50 text-fsgu-green-700'
                  : isDarkMode
                  ? 'hover:bg-fsgu-gray-700 text-fsgu-silver-200'
                  : 'hover:bg-fsgu-silver-50 text-fsgu-silver-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;